import { motion } from "framer-motion";
import { MessageCircle, Phone, MapPin, Instagram } from "lucide-react";
import { WA_LINK, PHONE_TEL, PHONE_DISPLAY, INSTA_LINK } from "@/lib/contact";

const EASE = [0.16, 1, 0.3, 1];

export default function Footer() {
  return (
    <footer
      id="contact"
      className="relative bg-[#0F1A1C] text-white overflow-hidden grain"
      data-testid="footer-section"
    >
      <div className="absolute -top-40 left-1/3 w-[560px] h-[560px] rounded-full bg-[radial-gradient(circle,rgba(108,190,69,0.16),transparent_65%)] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-20 lg:pt-28 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 34 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="rounded-[2rem] border border-[#D4AF37]/25 bg-gradient-to-br from-white/[0.06] to-transparent backdrop-blur p-10 lg:p-14 text-center mb-16"
        >
          <p className="font-mono-accent text-xs font-semibold uppercase tracking-[0.28em] text-[#82CB7E] mb-4">
            Ready when you are
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08]">
            Your fresh & dust free home is{" "}
            <span className="font-serif-accent italic font-semibold text-[#D4AF37]">
              one message away
            </span>
          </h2>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-cta"
              className="wa-pulse inline-flex items-center gap-3 rounded-full bg-[#25D366] text-white font-bold px-8 py-4 hover:bg-[#1eb857] transition-colors duration-300"
            >
              <MessageCircle className="w-5 h-5" />
              Contact Us on WhatsApp
            </a>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-call-cta"
              className="inline-flex items-center gap-2.5 rounded-full border-2 border-white/20 px-7 py-[14px] font-semibold hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300"
            >
              <Phone className="w-4.5 h-4.5" />
              {PHONE_DISPLAY}
            </a>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-10 lg:gap-16 items-start">
          <div>
            <div className="flex items-center gap-3">
              <img
                src="/assets/logo-round.png"
                alt="PK Hygiene logo"
                className="w-12 h-12 rounded-full ring-2 ring-[#D4AF37]/60"
              />
              <div className="leading-none">
                <p className="font-extrabold text-lg">
                  PK <span className="text-[#6CBE45]">Hygiene</span>
                </p>
                <p className="text-[10px] font-mono-accent uppercase tracking-[0.22em] text-white/50 mt-1">
                  Deep Cleaning Services
                </p>
              </div>
            </div>
            <p className="mt-5 text-sm text-white/60 leading-relaxed max-w-xs">
              Trusted professional deep cleaning for homes, villas, flats and
              offices — serving Angamaly, Chalakudy, Perumbavoor, Koratty and
              surrounding major regions since 2023.
            </p>
          </div>

          <div className="space-y-4" data-testid="footer-contact-details">
            <p className="font-mono-accent text-xs font-semibold uppercase tracking-[0.25em] text-[#82CB7E]">
              Contact
            </p>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-phone-link"
              className="flex items-center gap-3 text-white/80 hover:text-[#D4AF37] transition-colors"
            >
              <Phone className="w-4.5 h-4.5 text-[#6CBE45]" />
              {PHONE_DISPLAY}
            </a>
            <p className="flex items-center gap-3 text-white/80">
              <MapPin className="w-4.5 h-4.5 text-[#6CBE45]" />
              Angamaly – Manjapra, Kerala
            </p>
            <p
              className="flex items-start gap-3 text-white/70 text-sm leading-relaxed"
              data-testid="footer-areas-served"
            >
              <MapPin className="w-4.5 h-4.5 text-[#6CBE45] mt-0.5 shrink-0" />
              Serving Angamaly, Chalakudy, Perumbavoor, Koratty, Karukutty,
              Athani, Nedumbassery, Kalady & surrounding areas.
            </p>
            <a
              href={INSTA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              className="flex items-center gap-3 text-white/80 hover:text-[#D4AF37] transition-colors"
            >
              <Instagram className="w-4.5 h-4.5 text-[#6CBE45]" />
              @pkhygiene
            </a>
          </div>

          <div className="space-y-4">
            <p className="font-mono-accent text-xs font-semibold uppercase tracking-[0.25em] text-[#82CB7E]">
              Services
            </p>
            <ul className="text-sm text-white/60 space-y-2.5">
              <li>Complete Home Deep Cleaning</li>
              <li>Post-Construction Deep Cleaning</li>
              <li>Medical Grade Deep Cleaning</li>
              <li>Pre-Function Deep Cleaning</li>
              <li>Pest Control for Termites</li>
              <li>Shampoo Washing — Sofas, Beds & Carpets</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} PK Hygiene Deep Cleaning Services. All rights reserved.
          </p>
          <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-white/40">
            for Fresh and Dust free Home
          </p>
        </div>
      </div>
    </footer>
  );
}
